/**
 * TreeGridComponent
 *
 * Enterprise-grade hierarchical data grid with:
 * - Inline editing
 * - Drag & drop (within grid and cross-grid)
 * - Keyboard navigation
 * - Selection with checkboxes
 * - Pagination / Infinite scroll
 * - Full accessibility
 *
 * @example
 * // Basic usage with select all
 * <TreeGridComponent
 *   columns={columns}
 *   data={data}
 *   showSelection={true}
 *   showSelectAll={true}
 *   onSelectionChange={(selectedIds) => {
 *     // selectedIds is an array of all selected row IDs
 *     const selectedRows = data.filter(row => selectedIds.includes(row.id));
 *   }}
 * />
 *
 * @example
 * // Usage with info and delete icons
 * <TreeGridComponent
 *   columns={columns}
 *   data={data}
 *   showInfoIcon={true}
 *   showDeleteIcon={true}
 *   onInfoClick={(rowId, row) => {
 *     // Handle info click
 *   }}
 *   onDeleteClick={(rowId, row) => {
 *     // Handle delete logic
 *   }}
 * />
 *
 * @example
 * // Cross-grid drag and drop - Source grid (drag OUT only, no reordering)
 * // Use disableInternalDrop to prevent reordering within the source grid
 * <TreeGridComponent
 *   gridId="source-grid"
 *   columns={columns}
 *   data={sourceData}
 *   enableDragDrop={true}
 *   disableInternalDrop={true}  // Prevents reordering within this grid
 *   acceptExternalDrop={false}   // Source grid doesn't accept drops
 * />
 *
 * @example
 * // Cross-grid drag and drop - Target grid (accepts drops from source)
 * <TreeGridComponent
 *   gridId="target-grid"
 *   columns={columns}
 *   data={targetData}
 *   enableDragDrop={true}
 *   acceptExternalDrop={true}
 *   acceptDropFromGridIds={['source-grid']}
 *   onExternalDrop={(rows, sourceGridId, targetRowId, position) => {
 *     // Add rows to this grid
 *     setTargetData(prev => [...prev, ...rows]);
 *   }}
 * />
 *
 * @example
 * // Infinite scroll with server-side loading
 * <TreeGridComponent
 *   columns={columns}
 *   data={products}
 *   paginationMode="server"
 *   totalRows={totalCount}
 *   onLoadMore={loadMoreProducts}
 *   isLoadingMore={isLoading}
 *   hasMore={hasMoreProducts}
 *   maxHeight={400}
 * />
 */

import React, { useCallback, useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  TreeGridConfig,
  TreeGridColumn,
  TreeGridRow,
  ExternalDragData,
} from "./types";
import { useTreeGridState } from "./hooks/useTreeGridState";
import { useTreeKeyboard } from "./hooks/useTreeKeyboard";
import { getSelectionState } from "./utils/selectionUtils";
import { ComboBoxCellEditor } from "./editors/ComboBoxCellEditor";
import styles from "./TreeGridComponent.module.css";

/** MIME type for cross-grid drag and drop */
const TREEGRID_DRAG_MIME = "application/x-treegrid-row";

/** Footer action button configuration */
export interface FooterAction {
  /** Unique key for the action */
  key: string;
  /** Label text or function that receives selected count */
  label: string | ((selectedCount: number) => string);
  /** Click handler */
  onClick: () => void;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Button variant: 'primary' | 'secondary' | 'danger' */
  variant?: "primary" | "secondary" | "danger";
  /** Optional icon to show before label */
  icon?: React.ReactNode;
}

export interface TreeGridComponentProps<T = any> extends TreeGridConfig<T> {
  /** Optional title for the grid */
  title?: string;
  /** Whether to show the toolbar with actions */
  showToolbar?: boolean;
  /** @deprecated Use onLoadMore for infinite scroll instead */
  paginationMode?: "client" | "server";
  /** @deprecated Use onLoadMore for infinite scroll instead */
  totalPages?: number;
  /** Total rows count (displayed in footer) */
  totalRows?: number;
  /** Callback fired when user scrolls near the bottom to load more data */
  onLoadMore?: () => void;
  /** Whether more data is currently being loaded */
  isLoadingMore?: boolean;
  /** Whether there are more items to load */
  hasMore?: boolean;
  /** Maximum height for the table body (enables scroll). Default: 400px when onLoadMore is provided */
  maxHeight?: string | number;
  /** Whether to show selection checkboxes for each row (inherited from TreeGridConfig) */
  // showSelection?: boolean;
  /** Whether to show "select all" checkbox in header - when checked, selects all visible rows (inherited from TreeGridConfig) */
  // showSelectAll?: boolean;
  /** Whether to show the info icon for each row */
  showInfoIcon?: boolean;
  /** Whether to show the delete icon for each row */
  showDeleteIcon?: boolean;
  /** Whether to show the green tick (add/save) icon for each row */
  showAddIcon?: boolean;
  /** Whether to show the red cancel (X) icon for each row */
  showCancelIcon?: boolean;
  /** Callback when info icon is clicked */
  onInfoClick?: (rowId: string, row: TreeGridRow<T>) => void;
  /** Callback when delete icon is clicked */
  onDeleteClick?: (rowId: string, row: TreeGridRow<T>) => void;
  /** Callback when add/tick icon is clicked */
  onAddClick?: (rowId: string, row: TreeGridRow<T>) => void;
  /** Callback when cancel/X icon is clicked */
  onCancelClick?: (rowId: string, row: TreeGridRow<T>) => void;
  /** Whether to show delete confirmation dialog (default: true) */
  showDeleteConfirmation?: boolean;
  /** Custom title for delete confirmation dialog */
  deleteConfirmationTitle?: string;
  /** Custom message for delete confirmation dialog */
  deleteConfirmationMessage?: string | ((row: TreeGridRow<T>) => string);
  /** Custom confirm button text */
  deleteConfirmButtonText?: string;
  /** Custom cancel button text */
  deleteCancelButtonText?: string;
  /** Footer action buttons (shown when rows are selected) */
  footerActions?: FooterAction[];
  /** Extra rows to append at the bottom of tbody (e.g. inline add/edit form row) */
  appendedRows?: React.ReactNode;
}

export function TreeGridComponent<T = any>(props: TreeGridComponentProps<T>) {
  const { t } = useTranslation();
  const {
    title,
    showToolbar = true,
    columns,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    data: _data,
    showSelection = false,
    showSelectAll = false,
    enableDragDrop = true,
    enableKeyboardNav = true,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    enableInlineEdit: _enableInlineEdit = true,
    indentPerLevel = 24,
    loading = false,
    emptyMessage = "No data available",
    className = "",
    showGridLines = true,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    showHover: _showHover = true,
    striped = false,
    showInfoIcon = true,
    showDeleteIcon = true,
    showAddIcon = false,
    showCancelIcon = false,
    onInfoClick,
    onDeleteClick,
    onAddClick,
    onCancelClick,
    showDeleteConfirmation = true,
    deleteConfirmationTitle = "Remove file",
    deleteConfirmationMessage = "Are you sure you want to remove this file?",
    deleteConfirmButtonText = "Yes, remove!",
    deleteCancelButtonText = "No, keep it.",
    footerActions,
    // Cross-grid drag and drop props
    gridId,
    acceptExternalDrop = false,
    acceptDropFromGridIds,
    onExternalDrop,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onRowDraggedOut: _onRowDraggedOut,
    // Internal drop control - when true, prevents reordering within this grid
    disableInternalDrop = false,
    // Infinite scroll props
    onLoadMore,
    isLoadingMore = false,
    hasMore = false,
    maxHeight,
    onRowDoubleClick,
    disableDoubleClickEdit = false,
    infoIconVariant = "info",
    appendedRows,
  } = props;

  // Calculate effective max height - default to 400px when infinite scroll is enabled
  const effectiveMaxHeight =
    maxHeight !== undefined
      ? typeof maxHeight === "number"
        ? `${maxHeight}px`
        : maxHeight
      : onLoadMore
        ? "400px"
        : undefined;

  // State for delete confirmation dialog
  const [pendingDeleteRow, setPendingDeleteRow] =
    useState<TreeGridRow<T> | null>(null);

  // Handle delete with confirmation
  const handleDeleteWithConfirmation = useCallback(
    (rowId: string, row: TreeGridRow<T>) => {
      if (showDeleteConfirmation) {
        setPendingDeleteRow(row);
      } else {
        onDeleteClick?.(rowId, row);
      }
    },
    [showDeleteConfirmation, onDeleteClick],
  );

  // Confirm delete action
  const handleConfirmDelete = useCallback(() => {
    if (pendingDeleteRow) {
      onDeleteClick?.(pendingDeleteRow.id, pendingDeleteRow);
      setPendingDeleteRow(null);
    }
  }, [pendingDeleteRow, onDeleteClick]);

  // Cancel delete action
  const handleCancelDelete = useCallback(() => {
    setPendingDeleteRow(null);
  }, []);

  const state = useTreeGridState(props);
  const dragPreviewRef = useRef<HTMLDivElement>(null);
  const tableWrapperRef = useRef<HTMLDivElement>(null);

  // Use refs to avoid stale closure issues in scroll handler
  const onLoadMoreRef = useRef(onLoadMore);
  const isLoadingMoreRef = useRef(isLoadingMore);
  const hasMoreRef = useRef(hasMore);

  // Keep refs up to date
  useEffect(() => {
    onLoadMoreRef.current = onLoadMore;
    isLoadingMoreRef.current = isLoadingMore;
    hasMoreRef.current = hasMore;
  }, [onLoadMore, isLoadingMore, hasMore]);

  // Infinite scroll handler - using refs to avoid stale closures
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      const scrollBottom =
        target.scrollHeight - target.scrollTop - target.clientHeight;

      const currentOnLoadMore = onLoadMoreRef.current;
      const currentIsLoadingMore = isLoadingMoreRef.current;
      const currentHasMore = hasMoreRef.current;

      if (!currentOnLoadMore || currentIsLoadingMore || !currentHasMore) {
        return;
      }

      // Trigger load more when within 100px of bottom
      if (scrollBottom < 100) {
        currentOnLoadMore();
      }
    },
    [], // No dependencies - we use refs instead
  );
  const [dragPreviewData, setDragPreviewData] = useState<{
    row: TreeGridRow<T>;
    count: number;
  } | null>(null);

  // State for external drag (from another grid)
  const [externalDragOver, setExternalDragOver] = useState<{
    targetRowId: string | null;
    position: "before" | "after" | "child";
    sourceGridId: string;
  } | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);

  const {
    visibleRows,
    rowsById,
    selectedIds,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    expandedIds: _expandedIds,
    editingRowId,
    editingValues,
    focusedCell,
    dragState,
    validationErrors,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    page: _page,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    totalPages: _totalPages,
    allSelected,
    someSelected,
    handleToggleExpand,
    handleExpandAll,
    handleCollapseAll,
    handleToggleSelection,
    handleSelectAll,
    handleStartEdit,
    handleCancelEdit,
    handleCommitEdit,
    handleCellValueChange,
    handleMultiFieldChange,
    handleCreateRow,
    handleDeleteRow,
    handleIndent,
    handleOutdent,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    handlePageChange: _handlePageChange,
    getNavigationTarget,
    setFocusedCell,
  } = state;

  // Keyboard navigation
  const handleNavigate = useCallback(
    (direction: "up" | "down" | "left" | "right") => {
      if (!focusedCell) return;

      const target = getNavigationTarget(
        direction,
        focusedCell.rowId,
        focusedCell.columnKey,
      );
      if (target) {
        setFocusedCell(target);
      }
    },
    [focusedCell, getNavigationTarget, setFocusedCell],
  );

  const handleStartEditCurrent = useCallback(() => {
    if (!focusedCell) return;

    const focusedColumn = columns.find((c) => c.key === focusedCell.columnKey);

    if (focusedColumn?.editable) {
      // The focused column is editable – start editing it directly.
      handleStartEdit(focusedCell.rowId, focusedCell.columnKey);
    } else {
      // The focused column is not editable – jump to the first visible
      // editable column so Enter always activates edit mode.
      const firstEditableCol = columns.find((c) => c.editable && !c.hidden);
      if (firstEditableCol) {
        handleStartEdit(focusedCell.rowId, firstEditableCol.key);
      }
    }
  }, [focusedCell, columns, handleStartEdit]);

  const handleCommitEditCurrent = useCallback(() => {
    if (editingRowId) {
      handleCommitEdit();
    }
  }, [editingRowId, handleCommitEdit]);

  // Move focus to the next/previous focusable element outside the grid.
  // Called when the user presses Tab (or Shift+Tab) on a focused row.
  const handleTabOutOfGrid = useCallback(
    (shiftKey: boolean) => {
      setFocusedCell(null);

      const container = containerRef.current;
      if (!container) return;

      const focusableSelector = [
        "a[href]:not([disabled])",
        "button:not([disabled])",
        "input:not([disabled])",
        "select:not([disabled])",
        "textarea:not([disabled])",
        "[tabindex]:not([tabindex='-1'])",
      ].join(", ");

      // All focusable elements on the page that are outside this grid
      const outside = Array.from(
        document.querySelectorAll<HTMLElement>(focusableSelector),
      ).filter((el) => !container.contains(el));

      if (outside.length === 0) return;

      if (!shiftKey) {
        // Find the first focusable element that follows the container in DOM order
        const next = outside.find(
          (el) =>
            container.compareDocumentPosition(el) &
            Node.DOCUMENT_POSITION_FOLLOWING,
        );
        (next ?? outside[0]).focus();
      } else {
        // Find the last focusable element that precedes the container in DOM order
        const prev = [...outside]
          .reverse()
          .find(
            (el) =>
              container.compareDocumentPosition(el) &
              Node.DOCUMENT_POSITION_PRECEDING,
          );
        (prev ?? outside[outside.length - 1]).focus();
      }
    },
    [setFocusedCell],
  );

  useTreeKeyboard({
    enabled: enableKeyboardNav,
    focusedCell,
    editingRowId,
    defaultEditingRowId: props.defaultEditingRowId,
    visibleRows,
    columns,
    rowsById,
    selectedIds,
    enableIndentOutdent: props.enableIndentOutdent,
    enableKeyboardCreate: props.enableKeyboardCreate,
    onNavigate: handleNavigate,
    onStartEdit: handleStartEditCurrent,
    onCommitEdit: handleCommitEditCurrent,
    onCancelEdit: handleCancelEdit,
    onToggleExpand: handleToggleExpand,
    onToggleSelection: handleToggleSelection,
    onIndent: (rowId) => handleIndent(rowId),
    onOutdent: (rowId) => handleOutdent(rowId),
    onCreateRow: (parentId) => handleCreateRow(parentId, "simple"),
    onDeleteRow: handleDeleteRow,
    onTab: handleTabOutOfGrid,
  });

  // Check if we should accept a drop from a specific source grid
  const shouldAcceptDropFrom = useCallback(
    (sourceGridId: string): boolean => {
      if (!acceptExternalDrop) return false;
      if (sourceGridId === gridId) return false; // Don't accept from self
      if (!acceptDropFromGridIds || acceptDropFromGridIds.length === 0)
        return true; // Accept from all
      return acceptDropFromGridIds.includes(sourceGridId);
    },
    [acceptExternalDrop, gridId, acceptDropFromGridIds],
  );

  // Handle external drag over the grid container
  const handleExternalDragOver = useCallback(
    (e: React.DragEvent) => {
      if (!acceptExternalDrop) return;

      // Check if this is a treegrid drag
      const types = Array.from(e.dataTransfer.types);
      if (!types.includes(TREEGRID_DRAG_MIME)) return;

      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
    },
    [acceptExternalDrop],
  );

  // Handle external drop on the grid container (for dropping in empty area)
  const handleExternalContainerDrop = useCallback(
    (e: React.DragEvent) => {
      if (!acceptExternalDrop || !onExternalDrop) return;

      const jsonData = e.dataTransfer.getData(TREEGRID_DRAG_MIME);
      if (!jsonData) return;

      try {
        const dragData: ExternalDragData<T> = JSON.parse(jsonData);

        if (!shouldAcceptDropFrom(dragData.sourceGridId)) return;

        e.preventDefault();

        // Drop at root level
        const rowsToDrop =
          dragData.selectedRows && dragData.selectedRows.length > 0
            ? dragData.selectedRows
            : [dragData.row];

        onExternalDrop(rowsToDrop, dragData.sourceGridId, null, "after");
        setExternalDragOver(null);
      } catch (err) {
        console.error("Failed to parse external drag data:", err);
      }
    },
    [acceptExternalDrop, onExternalDrop, shouldAcceptDropFrom],
  );

  // Handle external drag leave
  const handleExternalDragLeave = useCallback((e: React.DragEvent) => {
    // Only clear if leaving the container entirely
    if (!containerRef.current?.contains(e.relatedTarget as Node)) {
      setExternalDragOver(null);
    }
  }, []);

  // ── Touch drag-and-drop (iPad / iOS) ──────────────────────────────────────
  // iOS/iPadOS do not support the HTML5 drag API, so we implement equivalent
  // behaviour via touch events attached with { passive: false } so we can
  // call preventDefault() to suppress native scroll during a drag.

  // Keep refs to the latest handler/state values so the touch useEffect
  // (which runs once) never captures stale closures.
  const handleDragStartRef = useRef(handleDragStart);
  const handleDragOverRef = useRef(handleDragOver);
  const handleDropRef = useRef(handleDrop);
  const handleDragEndRef = useRef(handleDragEnd);
  const setDragPreviewDataRef = useRef(setDragPreviewData);
  const rowsByIdRef = useRef(rowsById);
  const selectedIdsRef = useRef(selectedIds);
  const enableDragDropRef = useRef(enableDragDrop);
  const disableInternalDropRef = useRef(disableInternalDrop);

  useEffect(() => {
    handleDragStartRef.current = handleDragStart;
    handleDragOverRef.current = handleDragOver;
    handleDropRef.current = handleDrop;
    handleDragEndRef.current = handleDragEnd;
    setDragPreviewDataRef.current = setDragPreviewData;
    rowsByIdRef.current = rowsById;
    selectedIdsRef.current = selectedIds;
    enableDragDropRef.current = enableDragDrop;
    disableInternalDropRef.current = disableInternalDrop;
  }, [
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
    setDragPreviewData,
    rowsById,
    selectedIds,
    enableDragDrop,
    disableInternalDrop,
  ]);

  useEffect(() => {
    const wrapper = tableWrapperRef.current;
    if (!wrapper) return;

    const touchDragState: { rowId: string | null; isDragging: boolean } = {
      rowId: null,
      isDragging: false,
    };

    const onTouchStart = (e: TouchEvent) => {
      if (!enableDragDropRef.current) return;
      const target = e.target as HTMLElement;
      const handle = target.closest("[data-drag-handle]");
      if (!handle) return;

      const tr = (handle as HTMLElement).closest(
        "tr[data-row-id]",
      ) as HTMLElement | null;
      if (!tr) return;

      const rowId = tr.getAttribute("data-row-id");
      if (!rowId) return;

      e.preventDefault();
      touchDragState.rowId = rowId;
      touchDragState.isDragging = true;

      handleDragStartRef.current(rowId);

      const row = rowsByIdRef.current.get(rowId);
      if (row) {
        const selectedCount = selectedIdsRef.current.has(rowId)
          ? selectedIdsRef.current.size
          : 1;
        setDragPreviewDataRef.current({ row, count: selectedCount });
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!touchDragState.isDragging) return;
      e.preventDefault();

      const touch = e.touches[0];
      const elementUnderTouch = document.elementFromPoint(
        touch.clientX,
        touch.clientY,
      );
      if (!elementUnderTouch) return;

      const targetTr = (elementUnderTouch as HTMLElement).closest(
        "tr[data-row-id]",
      ) as HTMLElement | null;
      if (!targetTr) return;

      const targetRowId = targetTr.getAttribute("data-row-id");
      if (!targetRowId) return;

      const rect = targetTr.getBoundingClientRect();
      const y = touch.clientY - rect.top;
      const height = rect.height;

      let position: "before" | "after" | "child";
      if (y < height * 0.3) {
        position = "before";
      } else if (y > height * 0.7) {
        position = "after";
      } else {
        position = "child";
      }

      if (!disableInternalDropRef.current) {
        handleDragOverRef.current(targetRowId, position);
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (!touchDragState.isDragging) return;
      e.preventDefault();
      touchDragState.rowId = null;
      touchDragState.isDragging = false;

      handleDropRef.current();
      handleDragEndRef.current();
      setDragPreviewDataRef.current(null);
    };

    wrapper.addEventListener("touchstart", onTouchStart, { passive: false });
    wrapper.addEventListener("touchmove", onTouchMove, { passive: false });
    wrapper.addEventListener("touchend", onTouchEnd, { passive: false });
    wrapper.addEventListener("touchcancel", onTouchEnd, { passive: false });

    return () => {
      wrapper.removeEventListener("touchstart", onTouchStart);
      wrapper.removeEventListener("touchmove", onTouchMove);
      wrapper.removeEventListener("touchend", onTouchEnd);
      wrapper.removeEventListener("touchcancel", onTouchEnd);
    };
  }, []); // Runs once; latest values accessed via refs
  // ── End touch drag-and-drop ───────────────────────────────────────────────

  // Table CSS classes
  const tableClasses = [
    styles.table,
    !showGridLines && styles.noGridLines,
    striped && styles.striped,
  ]
    .filter(Boolean)
    .join(" ");

  const containerClasses = [
    styles.treeGridContainer,
    className,
    externalDragOver && styles.externalDragOver,
  ]
    .filter(Boolean)
    .join(" ");

  // Clear focused row highlight when focus moves outside the grid entirely
  const handleContainerBlur = useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      const relatedTarget = e.relatedTarget as Node | null;
      // relatedTarget is null when a child element (e.g. the inline editor
      // input) is unmounted and browser focus falls back to <body>. This is
      // NOT a deliberate tab-out by the user — keep the row highlighted so
      // Enter/Escape stays focused on the same row as required.
      if (relatedTarget === null) return;
      if (containerRef.current?.contains(relatedTarget)) return;
      setFocusedCell(null);
    },
    [setFocusedCell],
  );

  return (
    <div
      ref={containerRef}
      className={containerClasses}
      onDragOver={handleExternalDragOver}
      onDrop={handleExternalContainerDrop}
      onDragLeave={handleExternalDragLeave}
      onBlur={handleContainerBlur}
      data-grid-id={gridId}
      // tabIndex allows the container to receive programmatic focus so
      // :focus-within stays true while a row is highlighted by click
      tabIndex={-1}
      style={{ outline: "none" }}
    >
      {/* Header */}
      {showToolbar && (
        <div className={styles.treeGridHeader}>
          {title && <div className={styles.treeGridTitle}>{title}</div>}
          <div className={styles.treeGridActions}>
            <button
              onClick={() =>
                handleCreateRow(
                  focusedCell
                    ? (rowsById.get(focusedCell.rowId)?.parentId ?? null)
                    : null,
                  "simple",
                )
              }
              className={styles.paginationButton}
              title={t("add_new_row_shortcut", {
                ns: "layout_components",
                defaultValue: "Add new row (Ctrl+Enter)",
              })}
            >
              +{" "}
              {t("add_row", {
                ns: "layout_components",
                defaultValue: "Add Row",
              })}
            </button>
            <button
              onClick={handleExpandAll}
              className={styles.paginationButton}
              title={t("expand_all_rows", {
                ns: "layout_components",
                defaultValue: "Expand all rows",
              })}
            >
              {t("expand_all", {
                ns: "layout_components",
                defaultValue: "Expand All",
              })}
            </button>
            <button
              onClick={handleCollapseAll}
              className={styles.paginationButton}
              title={t("collapse_all_rows", {
                ns: "layout_components",
                defaultValue: "Collapse all rows",
              })}
            >
              {t("collapse_all", {
                ns: "layout_components",
                defaultValue: "Collapse All",
              })}
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div
        ref={tableWrapperRef}
        className={styles.tableWrapper}
        style={
          effectiveMaxHeight
            ? {
                maxHeight: effectiveMaxHeight,
                overflowY: "scroll",
                overflowX: "auto",
              }
            : undefined
        }
        onScroll={handleScroll}
      >
        {loading && (
          <div className={styles.loadingOverlay}>
            <div>
              {t("loading", {
                ns: "layout_components",
                defaultValue: "Loading...",
              })}
            </div>
          </div>
        )}

        {!loading && visibleRows.length === 0 && !appendedRows ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyStateText}>{emptyMessage}</div>
            <div className={styles.emptyStateSubtext}>
              {t("click_add_row_to_create_your_first_item", {
                ns: "layout_components",
                defaultValue: 'Click "Add Row" to create your first item',
              })}
            </div>
          </div>
        ) : (
          <table className={tableClasses}>
            <thead className={styles.tableHeader}>
              <tr className={styles.headerRow}>
                {/* Drag handle column */}
                {enableDragDrop && (
                  <th
                    className={`${styles.headerCell} ${styles.dragHandleHeaderCell}`}
                  >
                    {/* Empty header for drag handle column */}
                  </th>
                )}

                {/* Selection checkbox column */}
                {showSelection && (
                  <th
                    className={`${styles.headerCell} ${styles.checkboxHeaderCell}`}
                  >
                    {showSelectAll && (
                      <input
                        type="checkbox"
                        className={styles.checkbox}
                        checked={allSelected}
                        ref={(input) => {
                          if (input) {
                            input.indeterminate = someSelected && !allSelected;
                          }
                        }}
                        onChange={handleSelectAll}
                        aria-label="Select all rows"
                      />
                    )}
                  </th>
                )}

                {/* Column headers */}
                {columns
                  .filter((col) => !col.hidden)
                  .map((column) => (
                    <th
                      key={column.key}
                      className={`${styles.headerCell} ${
                        column.sortable ? styles.headerCellSortable : ""
                      }`}
                      style={{
                        width: column.width,
                        minWidth: column.minWidth,
                        maxWidth: column.maxWidth,
                        textAlign: column.align || "left",
                      }}
                      title={column.header}
                    >
                      <span className={styles.headerCellText}>
                        {column.header}
                      </span>
                    </th>
                  ))}

                {/* Action icons column */}
                {(showInfoIcon ||
                  showDeleteIcon ||
                  showAddIcon ||
                  showCancelIcon) && (
                  <th
                    className={`${styles.headerCell} ${styles.actionIconsHeaderCell}`}
                  >
                    {/* Empty header for action icons */}
                  </th>
                )}
              </tr>
            </thead>

            <tbody className={styles.tableBody}>
              {visibleRows.map((row) => (
                <TreeRow
                  key={row.id}
                  row={row}
                  columns={columns}
                  showSelection={showSelection}
                  indentPerLevel={indentPerLevel}
                  isSelected={selectedIds.has(row.id)}
                  selectionState={getSelectionState(
                    row.id,
                    selectedIds,
                    rowsById,
                  )}
                  isFocused={focusedCell?.rowId === row.id}
                  focusedColumnKey={
                    focusedCell?.rowId === row.id ? focusedCell.columnKey : null
                  }
                  isDragging={dragState?.draggedRowId === row.id}
                  isDropTarget={
                    // Show drop target indicator for:
                    // 1. Internal drags (only if internal drops are enabled)
                    // 2. External drags (from other grids)
                    (!disableInternalDrop &&
                      dragState?.dropTargetId === row.id) ||
                    externalDragOver?.targetRowId === row.id
                  }
                  dropPosition={
                    !disableInternalDrop && dragState?.dropTargetId === row.id
                      ? dragState.dropPosition
                      : null
                  }
                  isExternalDropTarget={
                    externalDragOver?.targetRowId === row.id
                  }
                  externalDropPosition={
                    externalDragOver?.targetRowId === row.id
                      ? externalDragOver.position
                      : null
                  }
                  enableDragDrop={enableDragDrop}
                  editingRowId={editingRowId}
                  editingValues={editingValues}
                  handleCellValueChange={handleCellValueChange}
                  handleCommitEdit={handleCommitEdit}
                  handleCancelEdit={handleCancelEdit}
                  validationErrors={validationErrors}
                  showInfoIcon={showInfoIcon}
                  showDeleteIcon={showDeleteIcon}
                  showAddIcon={showAddIcon}
                  showCancelIcon={showCancelIcon}
                  onInfoClick={onInfoClick}
                  onDeleteClick={handleDeleteWithConfirmation}
                  onAddClick={onAddClick}
                  onCancelClick={onCancelClick}
                  onToggleExpand={handleToggleExpand}
                  onToggleSelection={(rowId) => {
                    handleToggleSelection(rowId);
                    // Highlight the checked row and keep :focus-within alive
                    const firstVisibleCol = columns.find((c) => !c.hidden);
                    if (firstVisibleCol) {
                      setFocusedCell({ rowId, columnKey: firstVisibleCol.key });
                    }
                    containerRef.current?.focus();
                  }}
                  onMultiFieldChange={handleMultiFieldChange}
                  onCellClick={(columnKey) => {
                    setFocusedCell({ rowId: row.id, columnKey });
                    // Don't steal focus from an active cell editor (e.g. ComboBox):
                    // when the row is already being edited the editor's own input
                    // should keep focus.  Only move focus to the container for
                    // non-editing rows so :focus-within activates and the blur
                    // guard correctly treats subsequent internal clicks as staying
                    // inside the grid.
                    if (editingRowId !== row.id) {
                      containerRef.current?.focus();
                    }
                  }}
                  onCellDoubleClick={(columnKey) =>
                    handleStartEdit(row.id, columnKey)
                  }
                  onRowDoubleClick={onRowDoubleClick}
                  disableDoubleClickEdit={disableDoubleClickEdit}
                  infoIconVariant={infoIconVariant}
                  onDragStart={(e) => {
                    // Create custom drag preview data
                    const selectedCount = selectedIds.has(row.id)
                      ? selectedIds.size
                      : 1;
                    setDragPreviewData({ row, count: selectedCount });

                    handleDragStart(row.id);

                    // Set cross-grid drag data
                    if (gridId && e.dataTransfer) {
                      const selectedRows = selectedIds.has(row.id)
                        ? (Array.from(selectedIds)
                            .map((id) => rowsById.get(id))
                            .filter(Boolean) as TreeGridRow<T>[])
                        : [row];

                      const dragData: ExternalDragData<T> = {
                        sourceGridId: gridId,
                        row,
                        selectedRows,
                      };

                      e.dataTransfer.setData(
                        TREEGRID_DRAG_MIME,
                        JSON.stringify(dragData),
                      );
                      // Use a safe text representation for the drag
                      const rowData = row.data as Record<string, any>;
                      e.dataTransfer.setData(
                        "text/plain",
                        rowData?.product || rowData?.description || row.id,
                      );
                      e.dataTransfer.effectAllowed = "copyMove";
                    }

                    // Set custom drag image using the preview element (must be synchronous)
                    if (dragPreviewRef.current) {
                      const previewContent =
                        dragPreviewRef.current.querySelector(
                          `.${styles.dragPreviewContent}`,
                        );
                      if (previewContent && e.dataTransfer) {
                        try {
                          e.dataTransfer.setDragImage(
                            previewContent as HTMLElement,
                            20,
                            20,
                          );
                        } catch (err) {
                          console.warn("Could not set custom drag image:", err);
                        }
                      }
                    }
                  }}
                  onDragOver={(position) => handleDragOver(row.id, position)}
                  onDrop={handleDrop}
                  onDragEnd={() => {
                    handleDragEnd();
                    setDragPreviewData(null);
                  }}
                  // Cross-grid drop handlers
                  gridId={gridId}
                  acceptExternalDrop={acceptExternalDrop}
                  disableInternalDrop={disableInternalDrop}
                  onExternalDragOver={(position, sourceGridId) => {
                    if (shouldAcceptDropFrom(sourceGridId)) {
                      setExternalDragOver({
                        targetRowId: row.id,
                        position,
                        sourceGridId,
                      });
                    }
                  }}
                  onExternalDrop={(dragData, position) => {
                    if (
                      !onExternalDrop ||
                      !shouldAcceptDropFrom(dragData.sourceGridId)
                    )
                      return;

                    const rowsToDrop =
                      dragData.selectedRows && dragData.selectedRows.length > 0
                        ? dragData.selectedRows
                        : [dragData.row];

                    onExternalDrop(
                      rowsToDrop,
                      dragData.sourceGridId,
                      row.id,
                      position,
                    );
                    setExternalDragOver(null);
                  }}
                />
              ))}
              {appendedRows}
            </tbody>

            {/* Footer with totals */}
            {columns.some((col) => col.showTotal) && (
              <tfoot className={styles.tableFooter}>
                <tr className={styles.footerRow}>
                  {/* Empty cells for drag handle and checkbox columns */}
                  {enableDragDrop && <td className={styles.footerCell} />}
                  {showSelection && <td className={styles.footerCell} />}

                  {/* Column totals */}
                  {columns
                    .filter((col) => !col.hidden)
                    .map((column) => {
                      if (column.showTotal) {
                        // Calculate total for this column
                        const total = visibleRows.reduce((sum, row) => {
                          const value = (row.data as any)[column.key];
                          const numValue =
                            typeof value === "number"
                              ? value
                              : parseFloat(value) || 0;
                          return sum + numValue;
                        }, 0);

                        const formattedTotal = column.totalFormat
                          ? column.totalFormat(total)
                          : column.numberFormat
                            ? `${
                                column.numberFormat.prefix || ""
                              }${total.toLocaleString(undefined, {
                                minimumFractionDigits:
                                  column.numberFormat.decimals || 0,
                                maximumFractionDigits:
                                  column.numberFormat.decimals || 0,
                              })}${column.numberFormat.suffix || ""}`
                            : total.toLocaleString();

                        return (
                          <td
                            key={column.key}
                            className={styles.footerCell}
                            style={{ textAlign: column.align || "left" }}
                          >
                            <span className={styles.footerTotalLabel}>
                              {column.totalLabel || "Total:"}
                            </span>
                            <span className={styles.footerTotalValue}>
                              {formattedTotal}
                            </span>
                          </td>
                        );
                      }
                      return (
                        <td key={column.key} className={styles.footerCell} />
                      );
                    })}

                  {/* Empty cell for action icons column */}
                  {(showInfoIcon ||
                    showDeleteIcon ||
                    showAddIcon ||
                    showCancelIcon) && <td className={styles.footerCell} />}
                </tr>
              </tfoot>
            )}
          </table>
        )}
      </div>

      {/* Drag Preview - Always rendered for setDragImage to work */}
      <div ref={dragPreviewRef} className={styles.dragPreview}>
        <div className={styles.dragPreviewContent}>
          <div className={styles.dragPreviewRow}>
            {dragPreviewData ? (
              columns
                .filter((col) => !col.hidden)
                .slice(0, 3)
                .map((column) => (
                  <span key={column.key} className={styles.dragPreviewCell}>
                    {column.render
                      ? column.render(
                          (dragPreviewData.row.data as any)[column.key],
                          dragPreviewData.row,
                        )
                      : column.format
                        ? column.format(
                            (dragPreviewData.row.data as any)[column.key],
                            dragPreviewData.row,
                          )
                        : String(
                            (dragPreviewData.row.data as any)[column.key] || "",
                          )}
                  </span>
                ))
            ) : (
              <span className={styles.dragPreviewCell}>
                {t("dragging", {
                  ns: "layout_components",
                  defaultValue: "Dragging...",
                })}
              </span>
            )}
          </div>
          {dragPreviewData && dragPreviewData.count > 1 && (
            <div className={styles.dragPreviewBadge}>
              {dragPreviewData.count}
            </div>
          )}
        </div>
      </div>

      {/* Loading More Indicator */}
      {isLoadingMore && (
        <div className={styles.loadingMore}>
          <div className={styles.loadingMoreSpinner} />
          <span>
            {t("loading_more", {
              ns: "layout_components",
              defaultValue: "Loading more...",
            })}
          </span>
        </div>
      )}

      {/* Total rows info */}
      {props.totalRows !== undefined && props.totalRows > 0 && (
        <div className={styles.totalRowsInfo}>
          {t("showing_x_of_y_items", {
            ns: "layout_components",
            defaultValue: "Showing {{shown}} of {{total}} items",
            shown: visibleRows.length,
            total: props.totalRows.toLocaleString(),
          })}
          {hasMore && !isLoadingMore && (
            <button className={styles.loadMoreButton} onClick={onLoadMore}>
              {t("load_more", {
                ns: "layout_components",
                defaultValue: "Load More",
              })}
            </button>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {pendingDeleteRow && (
        <div className={styles.modalOverlay} onClick={handleCancelDelete}>
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-dialog-title"
          >
            {/* Close button */}
            <button
              className={styles.modalCloseButton}
              onClick={handleCancelDelete}
              aria-label="Close"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            {/* Warning icon */}
            <div className={styles.modalIconWrapper}>
              <svg
                className={styles.modalWarningIcon}
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                  stroke="#bc1306"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="#bc1306"
                />
                <circle cx="12" cy="16" r="1" fill="white" />
                <line
                  x1="12"
                  y1="9"
                  x2="12"
                  y2="13"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </div>

            {/* Title */}
            <h3 id="delete-dialog-title" className={styles.modalTitle}>
              {deleteConfirmationTitle}
            </h3>

            {/* Divider */}
            <div className={styles.modalDivider} />

            {/* Message */}
            <p className={styles.modalMessage}>
              {typeof deleteConfirmationMessage === "function"
                ? deleteConfirmationMessage(pendingDeleteRow)
                : deleteConfirmationMessage}
            </p>

            {/* Action buttons */}
            <div className={styles.modalActions}>
              <button
                className={styles.modalCancelButton}
                onClick={handleCancelDelete}
              >
                {deleteCancelButtonText}
              </button>
              <button
                className={styles.modalDeleteButton}
                onClick={handleConfirmDelete}
                autoFocus
              >
                <svg
                  className={styles.modalTrashIcon}
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  <line x1="10" y1="11" x2="10" y2="17" />
                  <line x1="14" y1="11" x2="14" y2="17" />
                </svg>
                {deleteConfirmButtonText}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer Actions Bar */}
      {footerActions && footerActions.length > 0 && selectedIds.size > 0 && (
        <div className={styles.footerActionsBar}>
          {footerActions.map((action) => (
            <button
              key={action.key}
              className={`${styles.footerActionButton} ${
                action.variant === "danger"
                  ? styles.footerActionButtonDanger
                  : ""
              }`}
              onClick={action.onClick}
              disabled={action.disabled}
            >
              {action.icon && (
                <span className={styles.footerActionIcon}>{action.icon}</span>
              )}
              {typeof action.label === "function"
                ? action.label(selectedIds.size)
                : action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Format a raw cell value as a number with thousands separator.
 * Only used as the final fallback when no `render` or `format` is defined
 * and the column declares `editorType: "number"` or supplies `numberFormat`.
 */
function formatNumericCell(value: any, column: TreeGridColumn): string {
  if (value === "" || value === null || value === undefined) return "";
  const num = parseFloat(String(value));
  if (isNaN(num)) return String(value);
  const {
    decimals,
    thousandsSeparator = true,
    prefix = "",
    suffix = "",
  } = column.numberFormat ?? {};
  const formatted = num.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: thousandsSeparator !== false,
  });
  return `${prefix}${formatted}${suffix}`;
}

/**
 * TreeRow Component
 *
 * Renders a single row in the TreeGrid with support for:
 * - Drag handle for reordering
 * - Selection checkbox
 * - Expand/collapse for hierarchical data
 * - Inline editing
 * - Cross-grid drag and drop
 */
interface TreeRowProps<T = any> {
  /** The row data to render */
  row: TreeGridRow<T>;
  /** Column definitions */
  columns: TreeGridColumn[];
  /** Whether to show selection checkbox */
  showSelection: boolean;
  /** Pixels to indent per tree level */
  indentPerLevel: number;
  /** Whether this row is selected */
  isSelected: boolean;
  /** Selection state for cascading selection */
  selectionState: "selected" | "partial" | "unselected";
  /** Whether this row has focus */
  isFocused: boolean;
  /** The key of the focused column (only relevant when isFocused is true) */
  focusedColumnKey: string | null;
  /** Whether this row is being dragged */
  isDragging: boolean;
  /** Whether this row is a drop target */
  isDropTarget: boolean;
  /** Drop position indicator - where the dragged item will be placed */
  dropPosition: "before" | "after" | "child" | null;
  /** Whether this row is an external drop target (from another grid) */
  isExternalDropTarget?: boolean;
  /** External drop position indicator */
  externalDropPosition?: "before" | "after" | "child" | null;
  /** Whether drag and drop is enabled */
  enableDragDrop: boolean;
  /** ID of the row currently being edited */
  editingRowId: string | null;
  /** Current editing values */
  editingValues: Record<string, any>;
  /** Callback when a cell value changes during editing */
  handleCellValueChange: (columnKey: string, value: any) => void;
  /** Callback to commit edit changes */
  handleCommitEdit: () => void;
  /** Callback to cancel editing */
  handleCancelEdit: () => void;
  /** Validation errors by row and column */
  validationErrors: Map<string, Map<string, string>>;
  /** Whether to show info icon */
  showInfoIcon?: boolean;
  /** Whether to show delete icon */
  showDeleteIcon?: boolean;
  /** Whether to show the green tick (add/save) icon */
  showAddIcon?: boolean;
  /** Whether to show the red cancel (X) icon */
  showCancelIcon?: boolean;
  /** Callback when info icon is clicked */
  onInfoClick?: (rowId: string, row: TreeGridRow<T>) => void;
  /** Callback when delete icon is clicked */
  onDeleteClick?: (rowId: string, row: TreeGridRow<T>) => void;
  /** Callback when add/tick icon is clicked */
  onAddClick?: (rowId: string, row: TreeGridRow<T>) => void;
  /** Callback when cancel/X icon is clicked */
  onCancelClick?: (rowId: string, row: TreeGridRow<T>) => void;
  /** Callback to toggle row expansion */
  onToggleExpand: (rowId: string) => void;
  /** Callback to toggle row selection */
  onToggleSelection: (rowId: string) => void;
  /** Callback when a cell is clicked */
  onCellClick: (columnKey: string) => void;
  /** Callback when a cell is double-clicked */
  onCellDoubleClick: (columnKey: string) => void;
  /** Optional callback when a row cell is double-clicked */
  onRowDoubleClick?: (row: TreeGridRow<T>, columnKey: string) => void;
  /** Disable built-in double-click edit behavior */
  disableDoubleClickEdit?: boolean;
  /** Visual variant for info action icon */
  infoIconVariant?: "info" | "edit";
  /** Callback when drag starts */
  onDragStart: (e: React.DragEvent) => void;
  /** Callback for internal drag over events */
  onDragOver: (position: "before" | "after" | "child") => void;
  /** Callback for internal drop events */
  onDrop: () => void;
  /** Callback when drag ends */
  onDragEnd: () => void;

  // Cross-grid drag and drop props
  /** Unique identifier for this grid (for cross-grid operations) */
  gridId?: string;
  /** Whether this grid accepts drops from external grids */
  acceptExternalDrop?: boolean;
  /** When true, prevents internal drops (reordering) within this grid */
  disableInternalDrop?: boolean;
  /** Callback for external drag over events */
  onExternalDragOver?: (
    position: "before" | "after" | "child",
    sourceGridId: string,
  ) => void;
  /** Callback for external drop events */
  onExternalDrop?: (
    dragData: ExternalDragData<T>,
    position: "before" | "after" | "child",
  ) => void;
  /** Callback to update multiple fields at once (for combobox/custom editors) */
  onMultiFieldChange?: (changes: Record<string, any>) => void;
}

function TreeRow<T = any>(props: TreeRowProps<T>) {
  const {
    row,
    columns,
    showSelection,
    indentPerLevel,
    isSelected,
    selectionState,
    isFocused,
    focusedColumnKey,
    isDragging,
    isDropTarget,
    dropPosition,
    isExternalDropTarget,
    externalDropPosition,
    enableDragDrop,
    editingRowId,
    editingValues,
    handleCellValueChange,
    handleCommitEdit,
    handleCancelEdit,
    validationErrors,
    showInfoIcon,
    showDeleteIcon,
    showAddIcon,
    showCancelIcon,
    onInfoClick,
    onDeleteClick,
    onAddClick,
    onCancelClick,
    onToggleExpand,
    onToggleSelection,
    onCellClick,
    onCellDoubleClick,
    onRowDoubleClick,
    disableDoubleClickEdit = false,
    infoIconVariant = "info",
    onDragStart,
    onDragOver,
    onDrop,
    onDragEnd,
    // Cross-grid props
    gridId,
    acceptExternalDrop,
    disableInternalDrop,
    onExternalDragOver,
    onExternalDrop,
    // Multi-field change support
    onMultiFieldChange,
  } = props;

  const dragHandleRef = useRef<HTMLButtonElement>(null);

  const rowClasses = [
    styles.tableRow,
    isSelected && styles.selected,
    isFocused && styles.focused,
    isDragging && styles.dragging,
    // Generic drop target for backward compatibility
    isDropTarget && styles.dropTarget,
    isExternalDropTarget && styles.externalDropTarget,
    // Position-specific drop indicators
    isDropTarget && dropPosition === "before" && styles.dropTargetBefore,
    isDropTarget && dropPosition === "after" && styles.dropTargetAfter,
    isDropTarget && dropPosition === "child" && styles.dropTargetChild,
    // External position-specific indicators
    isExternalDropTarget &&
      externalDropPosition === "before" &&
      styles.dropTargetBefore,
    isExternalDropTarget &&
      externalDropPosition === "after" &&
      styles.dropTargetAfter,
    isExternalDropTarget &&
      externalDropPosition === "child" &&
      styles.dropTargetChild,
  ]
    .filter(Boolean)
    .join(" ");

  const hasChildren = row.childrenIds.length > 0;

  /**
   * Handle drag over for both internal and external drags.
   * Determines the drop position (before, after, or child) based on cursor location.
   * Respects disableInternalDrop to prevent internal reordering when configured.
   *
   * Drop zones (inspired by Adobe Aria kanban):
   * - Top 30%: "before" - insert before this row (same level)
   * - Middle 40%: "child" - make child of this row (nest under)
   * - Bottom 30%: "after" - insert after this row (same level)
   */
  const handleRowDragOver = useCallback(
    (e: React.DragEvent) => {
      // Calculate drop position based on cursor position within row
      const rect = e.currentTarget.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const height = rect.height;

      let position: "before" | "after" | "child";
      // Adjusted zones: 30% top = before, 40% middle = child, 30% bottom = after
      // This makes it easier to target before/after positions for reordering
      if (y < height * 0.3) {
        position = "before";
      } else if (y > height * 0.7) {
        position = "after";
      } else {
        position = "child";
      }

      // Check if this is an external drag (from another grid)
      const types = Array.from(e.dataTransfer.types);
      const isExternalDrag = types.includes(TREEGRID_DRAG_MIME);

      if (isExternalDrag && acceptExternalDrop) {
        // External drag - allow drop and show visual feedback
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
        onExternalDragOver?.(position, "external"); // Source validated on drop
      } else if (!disableInternalDrop) {
        // Internal drag - only allow if internal drops are enabled
        e.preventDefault();
        onDragOver(position);
      }
      // If disableInternalDrop is true and it's an internal drag, don't preventDefault
      // This prevents the drop target indicator from showing
    },
    [onDragOver, onExternalDragOver, acceptExternalDrop, disableInternalDrop],
  );

  /**
   * Handle drop for both internal and external drags.
   * External drops (from other grids) take priority over internal drops.
   * Respects disableInternalDrop to prevent internal reordering.
   */
  const handleRowDrop = useCallback(
    (e: React.DragEvent) => {
      // Check for external drag data first (from another grid)
      const jsonData = e.dataTransfer.getData(TREEGRID_DRAG_MIME);

      if (jsonData && acceptExternalDrop && onExternalDrop) {
        try {
          const dragData: ExternalDragData<T> = JSON.parse(jsonData);

          // Only accept drops from different grids (cross-grid drop)
          if (dragData.sourceGridId !== gridId) {
            e.preventDefault();
            e.stopPropagation();

            // Use the same 30/70 thresholds as handleRowDragOver so that the
            // drop always lands in the same zone as the visual indicator.
            const rect = e.currentTarget.getBoundingClientRect();
            const y = e.clientY - rect.top;
            const height = rect.height;

            let position: "before" | "after" | "child";
            if (y < height * 0.3) {
              position = "before";
            } else if (y > height * 0.7) {
              position = "after";
            } else {
              position = "child";
            }

            onExternalDrop(dragData, position);
            return;
          }
        } catch (err) {
          console.error("Failed to parse external drag data:", err);
        }
      }

      // Internal drop - only process if internal drops are enabled
      if (!disableInternalDrop) {
        onDrop();
      }
    },
    [acceptExternalDrop, gridId, onExternalDrop, onDrop, disableInternalDrop],
  );

  return (
    <tr
      className={rowClasses}
      data-row-id={row.id}
      onDragOver={handleRowDragOver}
      onDrop={handleRowDrop}
      onDragEnd={onDragEnd}
    >
      {/* Drag handle */}
      {enableDragDrop && (
        <td className={`${styles.tableCell} ${styles.dragHandleCell}`}>
          <button
            ref={dragHandleRef}
            className={styles.dragHandle}
            draggable
            data-drag-handle="true"
            onDragStart={(e) => {
              e.stopPropagation();
              onDragStart(e);
            }}
            aria-label="Drag to reorder"
            title="Drag to reorder"
          >
            <svg
              viewBox="0 0 10 16"
              width="10"
              height="16"
              fill="currentColor"
              aria-hidden="true"
            >
              <circle cx="2" cy="3" r="1.5" />
              <circle cx="7" cy="3" r="1.5" />
              <circle cx="2" cy="8" r="1.5" />
              <circle cx="7" cy="8" r="1.5" />
              <circle cx="2" cy="13" r="1.5" />
              <circle cx="7" cy="13" r="1.5" />
            </svg>
          </button>
        </td>
      )}

      {/* Selection checkbox */}
      {showSelection && (
        <td className={`${styles.tableCell} ${styles.checkboxCell}`}>
          <input
            type="checkbox"
            className={`${styles.checkbox} ${
              selectionState === "partial" ? styles.checkboxPartial : ""
            }`}
            checked={isSelected}
            ref={(input) => {
              if (input) {
                input.indeterminate = selectionState === "partial";
              }
            }}
            onChange={() => onToggleSelection(row.id)}
            aria-label={`Select row ${row.id}`}
          />
        </td>
      )}

      {/* Data cells */}
      {columns
        .filter((col) => !col.hidden)
        .map((column, columnIndex) => {
          const isTreeColumn = column.isTreeColumn;
          const isRowEditing = editingRowId === row.id;
          const isEditing = isRowEditing && column.editable;
          const cellError = validationErrors.get(row.id)?.get(column.key);

          return (
            <td
              key={column.key}
              className={`${styles.tableCell} ${
                isEditing ? styles.editing : ""
              }`}
              style={{
                width: column.width,
                minWidth: column.minWidth,
                maxWidth: column.maxWidth,
                textAlign: column.align || "left",
              }}
              onClick={() => onCellClick(column.key)}
              onDoubleClick={() => {
                const keyToEdit = column.editable
                  ? column.key
                  : columns.filter((c) => !c.hidden).find((c) => c.editable)
                      ?.key;
                if (!keyToEdit) return;
                onRowDoubleClick?.(row, keyToEdit);
                if (!disableDoubleClickEdit) {
                  onCellDoubleClick(keyToEdit);
                }
              }}
              data-cell-id={`${row.id}-${column.key}`}
            >
              {isTreeColumn ? (
                <div className={styles.treeCell}>
                  {/* Indentation */}
                  <span
                    className={styles.treeIndent}
                    style={{ width: `${row.depth * indentPerLevel}px` }}
                  />

                  {/* Expand/collapse icon */}
                  {hasChildren ? (
                    <span
                      className={`${styles.expandIcon} ${
                        row.isExpanded ? styles.expanded : ""
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleExpand(row.id);
                      }}
                      role="button"
                      aria-label={row.isExpanded ? "Collapse" : "Expand"}
                      tabIndex={0}
                    >
                      {/* <svg viewBox="0 0 16 16" fill="currentColor">
                        <path d="M6 4l4 4-4 4z" />
                      </svg> */}

                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <path
                          d="M15 12.5L10 7.5L5 12.5"
                          stroke="#B0BEC5"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  ) : (
                    <span
                      className={`${styles.expandIcon} ${styles.placeholder}`}
                    />
                  )}
                  {/* Folder icon */}
                  {column.showFolderIcon && (
                    <span className={styles.folderIcon}>
                      <svg
                        viewBox="0 0 24 24"
                        width="20"
                        height="20"
                        fill="none"
                        stroke="#1976d2"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path
                          d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"
                          fill="#e3f2fd"
                        />
                      </svg>
                    </span>
                  )}

                  {/* Cell content */}
                  {isEditing ? (
                    <CellEditor
                      key={`${row.id}-${column.key}-tree-editor`}
                      column={column}
                      value={
                        editingValues[column.key] ??
                        (row.data as any)[column.key]
                      }
                      error={cellError}
                      shouldFocus={focusedColumnKey === column.key}
                      onChange={(value) =>
                        handleCellValueChange(column.key, value)
                      }
                      onCommit={handleCommitEdit}
                      onCancel={handleCancelEdit}
                      row={row}
                      onMultiFieldChange={onMultiFieldChange}
                    />
                  ) : (
                    <span
                      className={`${styles.cellContent} ${
                        column.editable ? styles.cellContentEditable : ""
                      }`}
                    >
                      {(() => {
                        const value = (row.data as any)[column.key];
                        if (isTreeColumn) {
                          //console.log('TreeColumn render:', { rowId: row.id, columnKey: column.key, value, data: row.data });
                        }
                        return column.render
                          ? column.render(value, row)
                          : column.format
                            ? column.format(value, row)
                            : column.editorType === "number" ||
                                column.numberFormat
                              ? formatNumericCell(value, column)
                              : String(value ?? "");
                      })()}
                    </span>
                  )}

                  {/* Validation error icon */}
                  {!isEditing && cellError && (
                    <span className={styles.validationError} title={cellError}>
                      ⚠️
                    </span>
                  )}
                </div>
              ) : (
                <>
                  {isEditing ? (
                    <>
                      <CellEditor
                        key={`${row.id}-${column.key}-editor`}
                        column={column}
                        value={
                          editingValues[column.key] ??
                          (row.data as any)[column.key]
                        }
                        error={cellError}
                        shouldFocus={focusedColumnKey === column.key}
                        onChange={(value) =>
                          handleCellValueChange(column.key, value)
                        }
                        onCommit={handleCommitEdit}
                        onCancel={handleCancelEdit}
                        row={row}
                        onMultiFieldChange={onMultiFieldChange}
                      />
                    </>
                  ) : (
                    <span
                      className={`${styles.cellContent} ${
                        column.editable ? styles.cellContentEditable : ""
                      }`}
                    >
                      {column.render
                        ? column.render((row.data as any)[column.key], row)
                        : column.format
                          ? column.format((row.data as any)[column.key], row)
                          : column.editorType === "number" ||
                              column.numberFormat
                            ? formatNumericCell(
                                (row.data as any)[column.key],
                                column,
                              )
                            : String((row.data as any)[column.key] ?? "")}
                    </span>
                  )}

                  {!isEditing && cellError && (
                    <span className={styles.validationError} title={cellError}>
                      ⚠️
                    </span>
                  )}
                </>
              )}
            </td>
          );
        })}

      {/* Action icons cell */}
      {(showInfoIcon || showDeleteIcon || showAddIcon || showCancelIcon) && (
        <td className={`${styles.tableCell} ${styles.actionIconsCell}`}>
          <div className={styles.actionIcons}>
            {showCancelIcon && row.id === editingRowId && (
              <button
                className={`${styles.actionIconButton} ${styles.cancelIconButton}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancelEdit();
                  onCancelClick?.(row.id, row);
                }}
                aria-label="Cancel"
                title="Cancel"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
            {showAddIcon && row.id === editingRowId && (
              <button
                className={`${styles.actionIconButton} ${styles.addIconButton}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCommitEdit();
                  onAddClick?.(row.id, row);
                }}
                aria-label="Save"
                title="Save"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </button>
            )}
            {showInfoIcon && (
              <button
                className={styles.actionIconButton}
                onClick={(e) => {
                  e.stopPropagation();
                  onInfoClick?.(row.id, row);
                }}
                aria-label={infoIconVariant === "edit" ? "Edit" : "View info"}
                title={infoIconVariant === "edit" ? "Edit" : "View info"}
              >
                {infoIconVariant === "edit" ? (
                  <svg
                    viewBox="0 0 24 24"
                    width="20"
                    height="20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 20h9" />
                    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                  </svg>
                ) : (
                  <svg
                    viewBox="0 0 24 24"
                    width="20"
                    height="20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                )}
              </button>
            )}
            {showDeleteIcon && (
              <button
                className={`${styles.actionIconButton} ${styles.deleteIconButton}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteClick?.(row.id, row);
                }}
                aria-label="Delete"
                title="Delete"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  <line x1="10" y1="11" x2="10" y2="17" />
                  <line x1="14" y1="11" x2="14" y2="17" />
                </svg>
              </button>
            )}
          </div>
        </td>
      )}
    </tr>
  );
}

// CellEditor Component
interface CellEditorProps<T = any> {
  column: TreeGridColumn<T>;
  value: any;
  error?: string;
  shouldFocus?: boolean;
  onChange: (value: any) => void;
  onCommit: () => void;
  onCancel: () => void;
  /** The row being edited - required for combobox and custom editors */
  row?: TreeGridRow<T>;
  /** Callback to update multiple fields at once */
  onMultiFieldChange?: (changes: Record<string, any>) => void;
}

function CellEditor<T = any>(props: CellEditorProps<T>) {
  const {
    column,
    value,
    error,
    shouldFocus = false,
    onChange,
    onCommit,
    onCancel,
    row,
    onMultiFieldChange,
  } = props;
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Only focus this input if it's the targeted cell
  React.useEffect(() => {
    if (shouldFocus && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [shouldFocus]);

  // Handle ComboBox editor type
  if (column.editorType === "combobox" && column.comboBoxConfig && row) {
    return (
      <ComboBoxCellEditor
        config={column.comboBoxConfig}
        value={value}
        displayValue={
          column.format
            ? String(column.format(value, row) || "")
            : String(value || "")
        }
        error={error}
        onChange={onChange}
        onMultiFieldChange={onMultiFieldChange}
        onCommit={onCommit}
        onCancel={onCancel}
        row={row}
        shouldFocus={shouldFocus}
      />
    );
  }

  // Handle custom editor type
  if (column.editorType === "custom" && column.customEditor && row) {
    const CustomEditor = column.customEditor;
    return (
      <CustomEditor
        value={value}
        row={row}
        column={column}
        onChange={onChange}
        onCommit={onCommit}
        onCancel={onCancel}
        onMultiFieldChange={onMultiFieldChange}
        error={error}
        shouldFocus={shouldFocus}
      />
    );
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onCommit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    }
  };

  // Simple text/number editor for now
  return (
    <input
      ref={inputRef}
      type={column.editorType === "number" ? "number" : "text"}
      className={`${styles.cellInput} ${error ? styles.error : ""}`}
      value={value ?? ""}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      autoFocus={shouldFocus}
      placeholder={column.autocompleteConfig?.placeholder}
    />
  );
}

// Re-export types for convenience
export type { ExternalDragData } from "./types";

export default TreeGridComponent;
